import { getHexTileMapKey } from '@/game/hexmap';
import type { Building, Command, Unit } from '@/game/sim';
import {
  assignedText,
  atHome,
  clearFocusCommand,
  durationText,
  type FocusRow,
  focusEntries,
  focusRows,
  unfocusTargetCommand,
} from '@/game/view/focusQueue';
import { knownTerritories } from '@/game/view/lastSeen';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';

/** Time to kill beyond this reads as a long job (warn), else ok. */
const LONG_TTK = 6;

function RowCard({
  row,
  owner,
  onRemove,
}: {
  row: FocusRow & { first: boolean };
  owner: { name: string; color: string } | null;
  onRemove: () => void;
}) {
  const hpFraction = row.maxHp > 0 ? row.hp / row.maxHp : 0;
  const tag = row.current ? 'now' : row.first ? 'next' : 'then';
  return (
    <div
      className={`flex flex-col gap-1 rounded-xl px-2 py-1.5 ${
        row.current ? 'bg-fill-hover ring-1 ring-danger/60' : 'bg-fill'
      } ${row.seen ? '' : 'opacity-60'}`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`grid h-4 w-4 shrink-0 place-items-center rounded-full text-[10px] font-bold tabular-nums text-black ${
            row.current ? 'bg-danger' : 'bg-danger/60'
          }`}
        >
          {row.index}
        </span>
        <span className="text-[11px] text-fg-muted">{row.glyph}</span>
        <span className="min-w-0 flex-1 truncate text-xs font-medium">
          {row.name}
          {owner && (
            <span className="font-normal text-fg-subtle">
              {' '}
              ·{' '}
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: owner.color }}
              />{' '}
              {owner.name}
            </span>
          )}
        </span>
        <span className={`text-[10px] ${row.current ? 'text-danger' : 'text-fg-subtle'}`}>
          {tag}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-icon h-5 w-5 text-[11px]"
          title="Remove from the focus queue"
          aria-label={`Remove ${row.name} from the focus queue`}
          onClick={onRemove}
        >
          ✕
        </button>
      </div>
      {row.seen && (
        <>
          <div className="h-1 w-full overflow-hidden rounded-full bg-fill">
            <div
              className={`h-full rounded-full ${hpFraction < 0.5 ? 'bg-warn' : 'bg-ok'}`}
              style={{ width: `${Math.min(1, hpFraction) * 100}%` }}
            />
          </div>
          <div className="flex items-baseline justify-between gap-2 text-[10px]">
            <span className="tabular-nums text-fg-muted">
              {Math.ceil(row.hp)}/{Math.ceil(row.maxHp)} hp
            </span>
            <span className="truncate text-fg-muted">{assignedText(row.report)}</span>
          </div>
          <div className="flex items-baseline justify-between text-[10px]">
            <span className="text-fg-subtle">time to kill</span>
            {row.ttk === null ? (
              <span className="text-fg-subtle">never</span>
            ) : (
              <span
                className={`font-medium tabular-nums ${row.ttk > LONG_TTK ? 'text-warn' : 'text-ok'}`}
              >
                ≈ {durationText(row.ttk)}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The focus queue of the selection (top right, left of the player list): its
 * attack targets in order with hp, who is on each, and the time to kill from
 * the assigned units' expected damage (`focusRows`). ✕ drops one target (or
 * advances if it is the current one), Clear drops the lot; the units then
 * pick targets themselves again. Shown only while the selection has a target.
 */
export function FocusQueuePanel({
  selectedUnitIds,
  dispatch,
}: {
  selectedUnitIds: readonly string[];
  dispatch: (command: Command) => void;
}) {
  const frame = useSimFrame();
  const { view: state, rules, playerId, fog, playerOrder } = frame;
  const entries = focusEntries(state, selectedUnitIds, playerId);
  if (entries.length === 0) return null;
  const isSeen = (target: Unit | Building) => {
    if ('buildProgress' in target) {
      return [target.pos, ...(target.footprint ?? [])].some((c) =>
        fog.visible.has(getHexTileMapKey(c))
      );
    }
    return fog.visible.has(getHexTileMapKey(target.pos));
  };
  // Built on the first seen unit row that needs it, not every render.
  let territories: ReturnType<typeof knownTerritories> | null = null;
  const isHome = (u: Unit) => {
    territories ??= knownTerritories(state, frame.knownBuildings(), playerId, rules, (key) =>
      fog.sight(key)
    );
    return atHome(territories, u);
  };
  const firstQueued = entries.find((e) => !e.current)?.targetId;
  const rows = focusRows(rules, state, entries, playerId, isSeen, isHome);
  const send = (command: Command | null) => command && dispatch(command);
  return (
    <div className="panel absolute right-[17.5rem] top-[3.75rem] z-10 flex max-h-[60vh] w-72 flex-col gap-1.5 p-2.5">
      <div className="flex items-center gap-2">
        <p className="panel-title whitespace-nowrap">Focus queue</p>
        <span className="chip tabular-nums">
          {rows.length} {rows.length === 1 ? 'target' : 'targets'}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          title="Clear the focus: the selection picks targets itself again"
          onClick={() => send(clearFocusCommand(state, selectedUnitIds, playerId))}
        >
          Clear ✕
        </button>
      </div>
      <div className="flex min-h-0 flex-col gap-1.5 overflow-y-auto">
        {rows.map((row) => {
          const ownerId = row.owner;
          return (
            <RowCard
              key={row.targetId}
              row={{ ...row, first: row.targetId === firstQueued }}
              owner={
                ownerId === null || ownerId === playerId
                  ? null
                  : {
                      name: frame.playerName(ownerId),
                      color: PLAYER_COLORS[colorIndexFor(ownerId, playerOrder)] ?? '#fff',
                    }
              }
              onRemove={() =>
                send(unfocusTargetCommand(state, selectedUnitIds, playerId, row.targetId))
              }
            />
          );
        })}
        <div className="flex items-center gap-2 px-1 pt-0.5 text-[11px] text-fg-muted">
          <span>then</span>
          <span className="chip bg-fill text-accent">auto (best target)</span>
        </div>
      </div>
      <div className="border-t border-line pt-1 text-[10px] text-fg-subtle">
        ⇧ + right-click queues a target · Backspace removes the last
      </div>
    </div>
  );
}
